import React, { useState } from 'react';
import { Box, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { KillParty, ZkbKill, formatIsk } from '../../api/killboard';
import { allianceLogo, characterPortrait, corporationLogo, shipRender, typeIcon } from '../../services/eveImages';
import { C, FONT, hexToRgba, lcarsLabelSx } from '../../theme/tokens';

/**
 * One kill, read as a duel: who lost what on the left, who landed the final
 * blow on the right, and the price in between. The line it replaces said the
 * same thing in a sentence — "Rekt Pilot (Gila) lost to Grix Voidpicker
 * (Loki)" — which reads fine once and scans terribly ten times, because
 * every fact sat at the same weight in the same typeface.
 *
 * The art does the scanning now: a hull render says what died before the
 * name is parsed, and the corp and alliance logos say who was involved
 * without a line of text each. Everything still links out to the killmail.
 */
const KillRow: React.FC<{ kill: ZkbKill }> = ({ kill: k }) => {
  const solo = k.attackerCount <= 1;

  return (
    <Box
      component="a"
      href={`https://zkillboard.com/kill/${k.killmail_id}/`}
      target="_blank"
      rel="noreferrer"
      sx={{
        display: 'grid',
        // Desktop reads left to right as the fight did: who died, how many
        // shot them, who landed it, what it cost. Mobile stacks the two
        // parties and pairs the numbers into one footer line.
        gridTemplateColumns: { xs: '1fr auto', md: 'minmax(0,1fr) auto minmax(0,1fr) auto' },
        alignItems: 'center',
        columnGap: 2,
        rowGap: { xs: 1.5, md: 1.25 },
        textDecoration: 'none',
        py: 1.5,
        px: 1,
        mx: -1,
        borderRadius: '8px',
        borderBottom: `1px solid ${C.borderSub}`,
        transition: 'background-color 0.18s ease',
        '&:hover': { backgroundColor: hexToRgba(C.red, 0.05) },
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Party party={k.victim} role="victim" sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }} />

      <Stack
        spacing={0.25}
        sx={{
          order: { xs: 3, md: 0 },
          gridColumn: { xs: '1', md: 'auto' },
          alignItems: { xs: 'flex-start', md: 'center' },
          flexShrink: 0,
          minWidth: 54,
        }}
      >
        <Typography sx={lcarsLabelSx(C.textDim, '0.6rem')}>attackers</Typography>
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.82rem', color: solo ? C.gold : C.text }}>
          {solo ? 'solo' : k.attackerCount}
        </Typography>
      </Stack>

      {k.finalBlow ? (
        <Party party={k.finalBlow} role="killer" sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }} />
      ) : (
        <Typography
          sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, color: C.textDim, fontFamily: FONT.mono, fontSize: '0.78rem' }}
        >
          no final blow recorded
        </Typography>
      )}

      <Stack
        spacing={0.25}
        sx={{ order: { xs: 4, md: 0 }, gridColumn: { xs: '2', md: 'auto' }, alignItems: 'flex-end', flexShrink: 0 }}
      >
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.92rem', color: C.text, whiteSpace: 'nowrap' }}>
          {formatIsk(k.totalValue)}
        </Typography>
        <Typography
          title={new Date(k.killmail_time).toLocaleString()}
          sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim, whiteSpace: 'nowrap' }}
        >
          {timeAgo(k.killmail_time)}
        </Typography>
      </Stack>
    </Box>
  );
};

/**
 * One side of the duel. Mirrored for the killer so the two clusters face each
 * other across the gang marker rather than reading as two identical rows —
 * the hulls end up nose to nose, which is what makes the row scan as a fight.
 */
const Party: React.FC<{ party: KillParty; role: 'victim' | 'killer'; sx?: SxProps<Theme> }> = ({ party, role, sx }) => {
  const victim = role === 'victim';
  const accent = victim ? C.red : C.cyan;
  // A citadel or a POCO has no character behind it, and "Unknown pilot" reads
  // as missing data rather than the fact it is: the corp lost the structure.
  const primaryName = party.characterId ? party.name : party.corporationName ?? party.name;

  return (
    <Stack
      direction={{ xs: 'row', md: victim ? 'row' : 'row-reverse' }}
      alignItems="center"
      spacing={1.25}
      sx={{ minWidth: 0, ...sx }}
    >
      <ShipArt party={party} accent={accent} />

      <Stack spacing={0.35} sx={{ minWidth: 0, alignItems: { xs: 'flex-start', md: victim ? 'flex-start' : 'flex-end' } }}>
        <Typography sx={{ ...lcarsLabelSx(accent, '0.6rem') }}>{victim ? 'victim' : 'final blow'}</Typography>

        <Stack
          direction={{ xs: 'row', md: victim ? 'row' : 'row-reverse' }}
          alignItems="center"
          spacing={0.75}
          sx={{ minWidth: 0, maxWidth: '100%' }}
        >
          {party.characterId && (
            <Portrait
              src={characterPortrait(party.characterId)}
              alt={party.name}
              title={party.name}
              size={22}
              radius="50%"
            />
          )}
          <Typography
            noWrap
            sx={{ color: victim ? C.text : C.red, fontWeight: 700, fontSize: '0.88rem', minWidth: 0 }}
          >
            {primaryName}
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: 'row', md: victim ? 'row' : 'row-reverse' }}
          alignItems="center"
          spacing={0.75}
          sx={{ minWidth: 0, maxWidth: '100%' }}
        >
          <Typography noWrap sx={{ fontFamily: FONT.mono, fontSize: '0.76rem', color: C.textDim, minWidth: 0 }}>
            {party.shipTypeName}
          </Typography>
          {/* Logo-only, with the name in the tooltip: two affiliation logos
              cost 36px, the same two names cost most of the row. */}
          {party.corporationId && (
            <Portrait
              src={corporationLogo(party.corporationId)}
              alt={party.corporationName ?? 'Corporation'}
              title={party.corporationName ?? 'Corporation'}
              size={16}
              radius="2px"
            />
          )}
          {party.allianceId && (
            <Portrait
              src={allianceLogo(party.allianceId)}
              alt={party.allianceName ?? 'Alliance'}
              title={party.allianceName ?? 'Alliance'}
              size={16}
              radius="2px"
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

/**
 * The hull, as CCP renders it. Falls back to the flat inventory icon when a
 * render does not exist — a citadel or a deployable is a real thing to lose,
 * and a broken image is worse than a plain one.
 */
const ShipArt: React.FC<{ party: KillParty; accent: string }> = ({ party, accent }) => {
  const [failed, setFailed] = useState(false);

  return (
    <Box
      sx={{
        width: 46,
        height: 46,
        flexShrink: 0,
        borderRadius: '6px',
        border: `1px solid ${hexToRgba(accent, 0.35)}`,
        backgroundColor: hexToRgba(C.surface, 0.6),
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
      }}
    >
      {party.shipTypeId ? (
        <Box
          component="img"
          loading="lazy"
          src={failed ? typeIcon(party.shipTypeId) : shipRender(party.shipTypeId)}
          onError={() => setFailed(true)}
          alt={party.shipTypeName}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : (
        // No hull id at all: keep the slot so the row's rhythm holds instead
        // of the text jumping left on one line out of ten.
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: C.textDim }}>?</Typography>
      )}
    </Box>
  );
};

/** A square of EVE art that hides itself if the image 404s, rather than
 * leaving a broken-image glyph in a row people are scanning. */
const Portrait: React.FC<{ src: string; alt: string; title: string; size: number; radius: string }> = ({
  src,
  alt,
  title,
  size,
  radius,
}) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;

  return (
    <Tooltip title={title} arrow>
      <Box
        component="img"
        loading="lazy"
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: radius,
          border: `1px solid ${C.hairline}`,
          display: 'block',
        }}
      />
    </Tooltip>
  );
};

/** "5h ago" beats a full locale timestamp for the question this list answers
 * — how fresh is this? The exact time stays available as the title. */
export function timeAgo(iso: string, now = Date.now()): string {
  const seconds = Math.max(0, (now - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default KillRow;
